import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { WhatsappLogo, ChatCircleText, Check, X, MagnifyingGlass, CaretDown, CaretUp } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { maskPhone } from '../utils/maskPhone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Clients Directory (Admin + Handler Settings).
 * Sections: Old Client + MB7..MB11. Per row: name + phone + Chat + WhatsApp + Select.
 * Bottom sticky bar → WhatsApp Broadcast to selected via wa.me sequential open.
 */
const ClientsDirectorySection = ({ actorRole }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ groups: {}, order: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set()); // customer_ids
  const [query, setQuery] = useState('');
  const [openSections, setOpenSections] = useState({ old: true });
  const [broadcast, setBroadcast] = useState(false);
  const [text, setText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let stopped = false;
    // Fix doc #4 — instant-cache: paint last-known directory, then refresh.
    const _c = readInstantCache('clients_directory', 'all');
    if (_c) setData(_c);
    const load = async () => {
      try {
        const res = await axios.get(`${API}/clients-directory`);
        if (stopped) return;
        setData(res.data);
        writeInstantCache('clients_directory', 'all', res.data);
        // Auto-open sections with content — only on the very first load so
        // subsequent poll refreshes don't reset the user's manual toggles.
        setOpenSections(prev => {
          if (Object.keys(prev).length > 1) return prev;
          const auto = {};
          Object.entries(res.data.groups || {}).forEach(([k, v]) => { if ((v || []).length > 0) auto[k] = true; });
          return { ...prev, ...auto };
        });
      } catch (e) {
        if (!loading) return;  // silent for background polls
        toast.error('Failed to load clients');
      } finally {
        if (!stopped) setLoading(false);
      }
    };
    load();
    // BIG-PLAN Point 3 — relaxed 3s → 30s. Handler edits already broadcast
    // via `customer_updated` socket event; this poll is a safety-net only.
    const iv = setInterval(load, 30000);
    return () => { stopped = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = query.trim().toLowerCase();
  // A "serial query" is anything starting with # or a pure number — we match
  // it against the client's permanent serial_number (exact or prefix).
  const rawQ = query.trim();
  const serialQ = rawQ.startsWith('#') ? rawQ.slice(1).trim() : (/^\d+$/.test(rawQ) ? rawQ : '');
  const filteredGroups = useMemo(() => {
    const out = {};
    (data.order || []).forEach(k => {
      const arr = (data.groups[k] || []).filter(c => {
        if (!q) return true;
        if (serialQ) {
          const sn = String(c.serial_number || '').replace(/^#/, '');
          return sn === serialQ || sn.startsWith(serialQ);
        }
        // Text search — matches name, phone, IFSC code, city, state,
        // district, branch, bank name, Client ID (universal search).
        const addr = c.client_address || {};
        return (c.personal_name || '').toLowerCase().includes(q)
          || (c.username || '').toLowerCase().includes(q)
          || (c.phone || '').includes(q)
          || (c.client_id || '').toLowerCase().includes(q)
          || (addr.ifsc || '').toLowerCase().includes(q)
          || (addr.city || '').toLowerCase().includes(q)
          || (addr.state || '').toLowerCase().includes(q)
          || (addr.district || '').toLowerCase().includes(q)
          || (addr.branch || '').toLowerCase().includes(q)
          || (addr.bank || '').toLowerCase().includes(q);
      });
      out[k] = arr;
    });
    return out;
  }, [data, q, serialQ]);

  const toggleSel = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAllInGroup = (key) => {
    const ids = (filteredGroups[key] || []).map(c => c.id);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const n = new Set(prev);
      if (allSelected) ids.forEach(id => n.delete(id));
      else ids.forEach(id => n.add(id));
      return n;
    });
  };
  const clearAll = () => setSelected(new Set());

  const openChat = (c) => {
    if (!c.room_id) { toast.info('No active chat room yet for this client'); return; }
    // Point 56 — pass "came from Client Directory" state so the chat's back
    // button returns here instead of falling through to the main dashboard.
    // Admin's Client Directory subview is URL-driven (?section=...), so the
    // exact section reopens; handler's settings hub isn't URL-driven, so
    // `from` there at least lands back on Settings instead of the dashboard.
    if (actorRole === 'super_admin') {
      navigate(`/admin-chat/${c.room_id}`, { state: { from: '/admin-settings?section=clients-directory' } });
    } else {
      navigate(`/handler-chat/${c.room_id}`, { state: { from: '/handler-settings' } });
    }
  };

  const openWA = (phone) => {
    const clean = String(phone || '').replace(/[^0-9]/g, '');
    if (!clean) { toast.error('No phone number'); return; }
    window.open(`https://wa.me/${clean}`, '_blank');
  };

  const doBroadcast = () => {
    const targets = [];
    (data.order || []).forEach(k => (data.groups[k] || []).forEach(c => { if (selected.has(c.id)) targets.push(c); }));
    if (targets.length === 0) { toast.error('Select at least one client'); return; }
    if (!text.trim()) { toast.error('Type a message'); return; }
    const encoded = encodeURIComponent(text.trim());
    // Sequential open with 800ms delay to avoid popup-blocker rejecting all tabs
    targets.forEach((c, i) => {
      const clean = String(c.phone || '').replace(/[^0-9]/g, '');
      if (!clean) return;
      setTimeout(() => window.open(`https://wa.me/${clean}?text=${encoded}`, '_blank'), i * 800);
    });
    toast.success(`Opening ${targets.length} WhatsApp tabs (allow pop-ups)`);
    setBroadcast(false); setText(''); setSelected(new Set());
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mt-6" data-testid="clients-directory-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <span className="text-xl">📇</span> Clients Directory
        </h3>
        {selected.size > 0 && (
          <button onClick={clearAll} className="text-xs text-red-600 hover:underline" data-testid="clients-clear-selection">Clear ({selected.size})</button>
        )}
      </div>

      <div className="relative mb-3">
        <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, phone, #serial, Client ID, IFSC, city or state…"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm"
          data-testid="clients-directory-search"
        />
      </div>

      {loading ? (
        <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
      ) : (
        <div className="space-y-2">
          {(data.order || []).map(key => {
            const arr = filteredGroups[key] || [];
            if (arr.length === 0 && q) return null;
            const label = key === 'old' ? 'Old Client' : key;
            const isOpen = !!openSections[key];
            const allSelected = arr.length > 0 && arr.every(c => selected.has(c.id));
            return (
              <div key={key} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`clients-group-${key}`}>
                <div className="flex items-center justify-between bg-gray-50 px-3 py-2">
                  <button onClick={() => setOpenSections(o => ({ ...o, [key]: !o[key] }))} className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    {isOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    <span>{label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{arr.length}</span>
                  </button>
                  {arr.length > 0 && (
                    <button
                      onClick={() => selectAllInGroup(key)}
                      data-testid={`clients-select-all-${key}`}
                      className={`text-[10px] px-2 py-1 rounded font-semibold ${allSelected ? 'bg-blue-600 text-white' : 'bg-white border text-blue-600'}`}
                    >{allSelected ? '✓ All Selected' : 'Select All'}</button>
                  )}
                </div>
                {isOpen && (
                  <div className="divide-y divide-gray-100">
                    {arr.map(c => {
                      const isSel = selected.has(c.id);
                      return (
                        <div key={c.id} className={`flex items-center gap-2 px-3 py-2 ${isSel ? 'bg-blue-50' : ''}`} data-testid={`clients-row-${c.id}`}>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSel(c.id)}
                            data-testid={`clients-checkbox-${c.id}`}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-800 truncate flex items-center gap-1.5">
                              {c.serial_number ? (
                                <span
                                  className="text-[10px] font-mono px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded flex-shrink-0"
                                  data-testid={`clients-serial-${c.id}`}
                                  title="Permanent serial number"
                                >#{c.serial_number}</span>
                              ) : null}
                              <span className="truncate">{c.personal_name || c.username}</span>
                            </div>
                            <div className="text-xs text-gray-500 truncate">{maskPhone(c.phone, user) || '-'} {c.mb_setup ? <span className="ml-1 px-1 bg-purple-100 text-purple-700 rounded text-[9px]">{c.mb_setup}</span> : null}</div>
                            {(c.client_address && (c.client_address.city || c.client_address.state)) && (
                              <div
                                className="text-[10px] text-blue-700 truncate mt-0.5"
                                title={c.client_address.formatted || c.client_address.address}
                                data-testid={`clients-ifsc-address-${c.id}`}
                              >
                                📍 {[c.client_address.city, c.client_address.state].filter(Boolean).join(', ')}
                                {c.client_address.branch ? <span className="text-gray-500"> · {c.client_address.branch}</span> : null}
                              </div>
                            )}
                          </div>
                          <button onClick={() => openChat(c)} className="p-1.5 hover:bg-blue-100 rounded-full text-blue-600" title="Open chat" data-testid={`clients-chat-btn-${c.id}`}>
                            <ChatCircleText size={18} weight="fill" />
                          </button>
                          <button onClick={() => openWA(c.phone)} className="p-1.5 hover:bg-green-100 rounded-full text-green-600" title="Open WhatsApp" data-testid={`clients-wa-btn-${c.id}`}>
                            <WhatsappLogo size={18} weight="fill" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky broadcast bar */}
      {selected.size > 0 && (
        <div className="sticky bottom-0 mt-4 -mx-6 -mb-6 p-3 bg-green-600 text-white flex items-center justify-between border-t border-green-700">
          <span className="text-sm font-semibold">{selected.size} selected</span>
          <button
            onClick={() => setBroadcast(true)}
            data-testid="clients-broadcast-btn"
            className="px-3 py-1.5 bg-white text-green-700 rounded text-xs font-bold flex items-center gap-1.5"
          >
            <WhatsappLogo size={14} weight="fill" /> WhatsApp Broadcast
          </button>
        </div>
      )}

      {/* Broadcast composer */}
      {broadcast && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" onClick={(e) => { if (e.target === e.currentTarget) setBroadcast(false); }}>
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl" data-testid="wa-broadcast-modal">
            <div className="px-4 py-3 bg-green-600 text-white flex items-center justify-between">
              <h4 className="font-bold flex items-center gap-2"><WhatsappLogo size={20} weight="fill" /> WhatsApp Broadcast</h4>
              <button onClick={() => setBroadcast(false)} className="p-1 hover:bg-white/20 rounded-full"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-xs text-gray-600">Message will open on WhatsApp for each of the <b>{selected.size}</b> selected clients. Allow pop-ups. Tabs open one every 0.8s.</p>
              <textarea
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your message…"
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                data-testid="wa-broadcast-text"
              />
              <div className="flex gap-2 pt-1">
                <button onClick={() => setBroadcast(false)} className="flex-1 py-2 border border-gray-300 rounded text-sm">Cancel</button>
                <button onClick={doBroadcast} className="flex-1 py-2 bg-green-600 text-white rounded text-sm font-bold flex items-center justify-center gap-1" data-testid="wa-broadcast-send-btn">
                  <Check size={14} /> Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsDirectorySection;
